// build-config.ts
// A catalog and a recipe in, a Config out. docs/reference/combinator-panel/

import type { Config } from './combinator';
import type { Catalog, CatalogElevation, CatalogItem, Dimension } from '../../shared/utils/elevation-catalog';

export type LayerSource = Dimension | ((elevation: CatalogElevation) => string | null);

export interface LayerRecipe {
  id: string;
  from: LayerSource;
  /** Fixes the option set and its order. Absent, the table decides. */
  options?: string[];
}

export interface Recipe {
  layers: LayerRecipe[];
  where?: (elevation: CatalogElevation) => boolean;
  state?: (match: CatalogElevation[]) => Record<string, unknown>;
}

const TABLES: Record<Dimension, 'homePlans' | 'roofs' | 'sidings' | 'styles'> = {
  plan: 'homePlans',
  roof: 'roofs',
  siding: 'sidings',
  style: 'styles'
};

const valueOf = (e: CatalogElevation, from: LayerSource): string | null =>
  typeof from === 'function' ? from(e) : e[from]?.slug ?? null;

const distinct = (values: Array<string | null>): string[] =>
  Array.from(new Set(values.filter((value): value is string => value !== null)));

/** The default state: the first match's fields, its entry, and the rest. */
export const defaultState = ([first, ...others]: CatalogElevation[]): Record<string, unknown> => ({
  ...first.fields,
  elevation: first,
  others
});

export function buildConfig(catalog: Catalog, recipe: Recipe): Config {
  const placed = (e: CatalogElevation): boolean =>
    recipe.layers.every((layer) => {
      const value = valueOf(e, layer.from);

      return value !== null && (!layer.options || layer.options.includes(value));
    });
  const included = catalog.elevations.filter((e) => (!recipe.where || recipe.where(e)) && placed(e));
  const groups = new Map<string, { when: Record<string, string>; match: CatalogElevation[] }>();

  included.forEach((e) => {
    const when = Object.fromEntries(recipe.layers.map((layer) => [layer.id, valueOf(e, layer.from) as string]));
    const key = JSON.stringify(when);
    const group = groups.get(key) || { when, match: [] };

    group.match.push(e);
    groups.set(key, group);
  });

  const optionsOf = (layer: LayerRecipe): string[] => {
    if (layer.options) return layer.options;
    if (typeof layer.from === 'function') return distinct(included.map((e) => valueOf(e, layer.from)));

    return (catalog[TABLES[layer.from]] as CatalogItem[]).map((item) => item.slug);
  };
  const shape = recipe.state || defaultState;

  return {
    layers: recipe.layers.map((layer) => ({ id: layer.id, options: optionsOf(layer) })),
    outcomes: Array.from(groups.values(), ({ when, match }) => ({ when, state: shape(match) }))
  };
}

/** The combinations two or more elevations share, for the debug print. */
export const collisions = (catalog: Catalog, recipe: Recipe): Record<string, string>[] =>
  buildConfig(catalog, recipe)
    .outcomes.filter((o) => ((o.state as { others?: unknown[] }).others || []).length > 0)
    .map((o) => o.when);
